// apps/amsuite/src/app/services/workspace-dashboard.adapter.ts
import {
  WorkspaceDashboardDto, WorkspaceDashboardWidgetDto,
  LegacyDashboard, LegacyDashboardWidget,
  CreateWorkspaceDashboardRequest, UpdateWorkspaceDashboardRequest,
  WorkspaceDashboardWidgetInput, CreateTemplateRequest, UpdateTemplateRequest
} from '../models';
import { resolveOwnerAppId } from '../constants/widget-owner-app.constant';

function extractSelector(d: WorkspaceDashboardWidgetDto): string {
  if (d.widgetSelector) return d.widgetSelector;
  try {
    const pos = d.position ? JSON.parse(d.position) : null;
    return pos?.selector ?? '';
  } catch {
    return '';
  }
}

export function adaptWorkspaceWidget(d: WorkspaceDashboardWidgetDto): LegacyDashboardWidget {
  const selector = extractSelector(d);
  return {
    id: d.widgetId,
    dashboardAssociationId: d.id,
    selector,
    ownerAppId: resolveOwnerAppId(selector),
    dashboardPosition: d.position ?? '{}',
    dashboardAssociationParamValue: d.paramValues ?? ''
  };
}

export function adaptWorkspaceDashboard(d: WorkspaceDashboardDto): LegacyDashboard {
  return {
    id: d.id,
    appId: d.appId,
    name: d.name,
    default: d.isDefault,
    widgets: (d.widgets ?? []).map(adaptWorkspaceWidget)
  };
}

export function adaptWorkspaceDashboards(list: WorkspaceDashboardDto[]): LegacyDashboard[] {
  return (list ?? []).map(adaptWorkspaceDashboard);
}

function toParamValues(raw: unknown): string | null {
  if (raw == null) return null;
  return typeof raw === 'string' ? raw : JSON.stringify(raw);
}

/**
 * Risolve l'id del widget (GUID di catalogo) dalla shape legacy.
 * Invariante: un widget deve sempre avere `id` o `idWidget`; in caso contrario
 * fallisce esplicitamente invece di inviare un widgetId undefined al backend.
 */
function resolveWidgetId(w: any): string {
  const id = w?.id ?? w?.idWidget;
  if (id == null || id === '') {
    throw new Error('Widget senza identificativo (id/idWidget): impossibile costruire la request Workspace /v1/dashboards.');
  }
  return id;
}

export function toUpdateRequest(legacy: any): UpdateWorkspaceDashboardRequest {
  return {
    name: legacy?.name ?? '',
    widgets: (legacy?.widgets ?? []).map((w: any) => ({
      widgetId: resolveWidgetId(w),
      position: w.dashboardPosition ?? null,
      paramValues: toParamValues(w.dashboardAssociationParamValue)
    }))
  };
}

export function toCreateRequest(appId: string, name: string, legacyWidgets: any[] = []): CreateWorkspaceDashboardRequest {
  return {
    appId,
    name,
    widgets: (legacyWidgets ?? []).map((w: any) => ({
      widgetId: resolveWidgetId(w),
      position: w.dashboardPosition ?? null,
      paramValues: toParamValues(w.dashboardAssociationParamValue)
    }))
  };
}

/**
 * Mappa la lista di widget legacy nello shape REST `WorkspaceDashboardWidgetInput[]`.
 * Riuso condiviso fra create dashboard, create/update template.
 */
export function toWidgetInputs(legacyWidgets: any[] = []): WorkspaceDashboardWidgetInput[] {
  return (legacyWidgets ?? []).map((w: any) => ({
    widgetId: resolveWidgetId(w),
    position: w.dashboardPosition ?? null,
    paramValues: toParamValues(w.dashboardAssociationParamValue)
  }));
}

/**
 * Costruisce il body per POST /v1/admin/templates/{tenant|system}.
 * Stesso mapping di `toCreateRequest` con in piu il flag `isDefault`.
 */
export function toTemplateCreateRequest(
  appId: string, name: string, legacyWidgets: any[] = [], isDefault = false
): CreateTemplateRequest {
  return {
    appId,
    name,
    widgets: toWidgetInputs(legacyWidgets),
    isDefault
  };
}

/**
 * Costruisce il body per PUT /v1/admin/templates/{tenant|system}/{id}.
 * `isDefault` opzionale: incluso solo se passato esplicitamente.
 */
export function toTemplateUpdateRequest(
  name: string, legacyWidgets: any[] = [], isDefault?: boolean
): UpdateTemplateRequest {
  const req: UpdateTemplateRequest = {
    name: name ?? '',
    widgets: toWidgetInputs(legacyWidgets)
  };
  if (isDefault !== undefined) {
    req.isDefault = isDefault;
  }
  return req;
}
